import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import {
  ExtraLargeTh,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import useDidUpdate from '@/hooks/useDidUpdate';
import useHasPermission from '@/hooks/useHasPermission';
import InvoicesSearchBar from '@/pages/InvoiceList/components/InvoicesSearchBar';
import {
  InvoiceResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import DeletedInvoicesTableRow from './DeletedInvoicesTableRow';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: 'receiver',
};

const StyledTable = styled(Table)<{ $seeActionColumn?: boolean }>`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    min-width: 120px;
    &:nth-child(1) {
      min-width: 162px;
      position: sticky;
      z-index: 1;
      padding-right: 24px;
      padding-left: 24px;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      min-width: 56px;
      width: 56px;
      z-index: 1;
      position: relative;
    }
  }
  ${({ $seeActionColumn }) =>
    $seeActionColumn &&
    css`
      &:last-child {
        min-width: 56px;
        width: 56px;
        z-index: 1;
        position: relative;
      }
    `}
  td {
    padding-top: 26px;
    padding-bottom: 26px;
    ${({ $seeActionColumn }) =>
      $seeActionColumn &&
      css`
        &:last-child {
          min-width: 56px;
          width: 56px;
          position: sticky;
          right: 0;
          top: 0;
          bottom: 0;
          z-index: 1;
          padding-right: 16px;
          padding-left: 16px;
          border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
        }
      `}
  }
`;

const StyledTableHeader = styled(TableHeader)`
  display: flex;
  justify-content: flex-end;
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin-top: 24px;
`;

const LargeSortableTh = styled(SortableTh)`
  min-width: 140px;
`;

const DeletedInvoicesTable = () => {
  const [search, setSearch] = useState<string>('');

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const userCanManageData = useHasPermission('manage invoices');

  const fetchInvoices = ({ pageParam = '/v1/payments/invoices-deleted' }) => {
    return api.get<never, PaginatedResponse<InvoiceResource>>(pageParam, {
      params: {
        ...(!!search && {
          'filter[search]': search,
        }),
        ...(sort.direction !== 'DEFAULT' && {
          sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    remove,
    refetch,
  } = useInfiniteQuery<PaginatedResponse<InvoiceResource>>(
    ['deleted_invoices_list'],
    fetchInvoices,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const invoices = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<InvoiceResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [sort, search]);

  return (
    <>
      <StyledTableHeader>
        <FullScreenButton />
      </StyledTableHeader>
      <InvoicesSearchBar setValue={setSearch} />
      <StyledSectionHeading>deleted list (last 30 days)</StyledSectionHeading>
      <ScrollableTable>
        <StyledTable $seeActionColumn={userCanManageData}>
          <thead>
            <tr>
              <LargeSortableTh
                property="receiver"
                title="Receiver"
                setSort={setSort}
                sort={sort}
              />
              <Th>Comments</Th>
              <SortableTh
                property="company"
                title="Company"
                setSort={setSort}
                sort={sort}
              />
              <>
                <LargeSortableTh
                  property="debtor_bank"
                  title="Debtor Bank"
                  setSort={setSort}
                  sort={sort}
                />
                <LargeSortableTh
                  property="bank"
                  title="Creditor Bank"
                  setSort={setSort}
                  sort={sort}
                />
                <LargeSortableTh
                  property="bank_country"
                  title="Creditor Bank Country"
                  setSort={setSort}
                  sort={sort}
                />
                <LargeSortableTh
                  property="payment_type"
                  title="Payment Type"
                  setSort={setSort}
                  sort={sort}
                />
              </>
              <LargeSortableTh
                property="department"
                title="Department"
                setSort={setSort}
                sort={sort}
              />
              <Th>Invoice ID</Th>
              <ExtraLargeTh>Finance Account Number</ExtraLargeTh>
              <Th>Amount (€)</Th>
              <Th>Amount ($)</Th>
              <Th>Amount (£)</Th>
              <SortableTh
                property="receivedDate"
                title="Received Date"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="deletedDate"
                title="Deleted Date"
                setSort={setSort}
                sort={sort}
              />
              <Th>Created by</Th>
              <Th>Last Edited</Th>
              <Th>Files</Th>
              {userCanManageData && (
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!invoices?.length}
            >
              {invoices.map((invoice) => (
                <DeletedInvoicesTableRow key={invoice.id} invoice={invoice} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default DeletedInvoicesTable;
